import { useApolloClient } from '@apollo/client/react';
import { ModDivider } from '@moderneinc/design-system-components';
import {
  borderRadius,
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { ButtonBase, Stack, Typography } from '@mui/material';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { signOut, useSession } from 'next-auth/react';
import { type FunctionComponent, useContext } from 'react';
import { ACCOUNT_SETTINGS_PATH } from '../../../constants/general';
import { ConfigurationContext } from '../../../contexts/config.context';
import { sanitizeDisplayName } from '../../../helpers/string.helper';
import { useCommandPalette } from '../../../hooks/use-command-palette.hooks';
import { UserAvatar } from '../../account-menu/user-avatar.component';
import { CustomerBranding } from '../../navigation/left-nav-items/customer-branding.component';
import { ScmConnections } from '../../scm-connection-status/scm-connection-status.component';
import { OverflowMenuCaption } from './overflow-menu-caption.styled';
import { OverflowMenuRow } from './overflow-menu-row.component';

type OverflowMenuAccountFooterProps = {
  onClose: () => void;
};

export const OverflowMenuAccountFooter: FunctionComponent<
  OverflowMenuAccountFooterProps
> = ({ onClose }) => {
  const { config } = useContext(ConfigurationContext);
  const { version } = config;
  const client = useApolloClient();
  const { push } = useRouter();
  const { data: session } = useSession();
  const handleLogout = async () => {
    await client.clearStore();
    window.location.href = '/api/auth/keycloak/logout';
    await signOut({ redirect: false });
  };

  useCommandPalette([
    {
      label: 'Sign Out',
      action: handleLogout
    },
    {
      label: 'Go to Account settings',
      action: (e) => {
        if (e && typeof e.preventDefault === 'function') {
          e.preventDefault();
        }
        return push(ACCOUNT_SETTINGS_PATH);
      },
      keyboardShortcuts: {
        key: '~',
        modifier: { shift: true }
      }
    }
  ]);

  if (!session?.user) {
    return null;
  }

  const fullName = sanitizeDisplayName(
    session.user.fullName ?? session.user.name ?? ''
  );
  const email = session.user.email ?? '';
  const userName = sanitizeDisplayName(session.user.userName ?? '');
  const showUserName =
    Boolean(userName) &&
    userName.toLowerCase() !== email.toLowerCase() &&
    userName.toLowerCase() !== fullName.toLowerCase();

  return (
    <Stack
      sx={{
        gap: `${spacing.spacing_1}px`
      }}
    >
      <ModDivider sx={{ my: 0 }} />
      {/* Clickable text block — name + email + handle */}
      <ButtonBase
        component={Link}
        href={ACCOUNT_SETTINGS_PATH}
        onClick={onClose}
        data-testid="overflow-menu-account-card"
        disableRipple
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          width: '100%',
          gap: `${spacing.spacing_1_1_2}px`,
          paddingTop: `${spacing.spacing_1}px`,
          paddingBottom: `${spacing.spacing_1}px`,
          paddingLeft: `${spacing.spacing_1_1_2}px`,
          paddingRight: `${spacing.spacing_1_1_2}px`,
          borderRadius: `${borderRadius.s}px`,
          textAlign: 'left',
          textDecoration: 'none',
          color: semanticColors.color.text.primary,
          '&:hover': {
            backgroundColor: semanticColors.color.surface.rowHover
          }
        }}
      >
        {config.brandingIcon ? (
          <CustomerBranding config={config} collapsed align="auto" />
        ) : (
          <UserAvatar />
        )}
        <Stack sx={{ alignItems: 'flex-start', minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: semanticColors.color.text.primary,
              lineHeight: 1.4
            }}
          >
            {fullName}
          </Typography>
          {email ? <OverflowMenuCaption>{email}</OverflowMenuCaption> : null}
          {showUserName ? (
            <OverflowMenuCaption>{userName}</OverflowMenuCaption>
          ) : null}
        </Stack>
      </ButtonBase>
      {/* Outside the ButtonBase — the SCM icons are separately clickable */}
      <Stack sx={{ paddingX: `${spacing.spacing_1_1_2}px` }}>
        <ScmConnections />
      </Stack>
      <ModDivider sx={{ my: 0 }} />
      {/* Copyright + version */}
      <Stack
        sx={{
          paddingX: `${spacing.spacing_1_1_2}px`,
          gap: 0
        }}
      >
        <OverflowMenuCaption>
          &copy; {new Date().getFullYear()} Moderne, Inc.
        </OverflowMenuCaption>
        {version ? (
          <OverflowMenuCaption>Version: {version}</OverflowMenuCaption>
        ) : null}
      </Stack>
      {/* Sign out */}
      <OverflowMenuRow
        label="Sign out"
        onClick={handleLogout}
        data-testid="overflow-menu-sign-out"
      />
    </Stack>
  );
};
